import { axiosInstance, axiosDownloadFileInstance } from "api/axios.instance"

class DonwloadFileService {
	async getPresignedDownloadUrl(url: string) {
		return axiosInstance.get(url)
	}

	async fileDownload(url: string, fileName: string, type: string): Promise<void> {
		const link = document.createElement("a")
		link.href = url
		link.style.display = "none"
		link.setAttribute("download", `${fileName}.${type}`)
		document.body.appendChild(link)
		link.click()
		link.remove()
	}

	// Forces a save-to-disk for a cross-origin URL (a presigned S3 link) by fetching it into a blob
	// first, rather than pointing an `<a download>` at it directly. The `download` attribute is only
	// honored by browsers for same-origin links — cross-origin, the browser instead follows the
	// response's own `Content-Disposition`, and a presigned S3 link that doesn't set `attachment`
	// opens the file inline in a new tab instead of downloading it. No auth header: the URL is
	// already pre-signed, so this deliberately does not go through `axiosInstance` — but it does go
	// through axios itself (`axiosDownloadFileInstance`) rather than a bare `fetch`, for
	// `onDownloadProgress` (the annotated result PDF and the Word file can both take a while to build
	// and transfer, so a caller wants to show the transfer's own progress rather than a toast that
	// looks stalled until the whole body has arrived). Same browser-native progress event
	// `uploadFiles.service.ts` already uses for the upload side, so this needs no hand-rolled
	// `ReadableStream` reader or byte counting: `event.progress` (0-1) is only set once the browser
	// can compute a total, so `onProgress` simply never fires when it can't — the caller is left
	// showing whatever it displayed before the transfer started, not a bar stuck at 0%.
	async downloadFromUrl(url: string, fileName: string, onProgress?: (fraction: number) => void): Promise<void> {
		const { data } = await axiosDownloadFileInstance.get<Blob>(url, {
			responseType: "blob",
			onDownloadProgress: onProgress
				? (event) => {
						if (event.progress !== undefined) onProgress(event.progress)
					}
				: undefined,
		})
		this.downloadBlob(data, fileName)
	}

	// Saves an already-fetched blob to disk. `fileDownload` above cannot serve authenticated API
	// endpoints: it points an <a href> at a URL, which sends no Authorization header — fine for
	// presigned S3 links, not for something like the on-demand result CSV (REQ-420), which must go
	// through axiosInstance first and arrive here as a blob. `fileName` is used as-is, extension
	// included — the caller composes the whole name.
	downloadBlob(blob: Blob, fileName: string): void {
		const objectUrl = URL.createObjectURL(blob)
		const link = document.createElement("a")
		link.href = objectUrl
		link.style.display = "none"
		link.setAttribute("download", fileName)
		// Appended at the page level on purpose: a click on a detached anchor (or one inside the
		// viewer's shadow root) does not reliably start a download in every browser.
		document.body.appendChild(link)
		link.click()
		link.remove()
		// Revoked on the next tick — revoking synchronously can cancel the download in progress.
		setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
	}
}

export default new DonwloadFileService()
